import { Reference } from '@approvalmax/types';
import { domain } from 'modules/data';
import { CompanySearchContext, TemplateReference } from 'modules/search';

interface SearchParametersXero {
    // Xero filters
    xeroDocumentNumber: string;
    xeroDocumentReference: string;
    xeroTrackingCategories: Array<{
        category: Reference;
        value: Reference;
    }>;
    xeroAccount: Reference | null;
    xeroItem: Reference | null;
    xeroContact: Reference | null;
    xeroNarration: string;
    airwallexScheduledPaymentDateGreaterEquals: string | null;
    airwallexScheduledPaymentDateLessEquals: string | null;
}

interface SearchParametersQbooks {
    // QBO filters
    qbooksPoNumber: string;
    qbooksDocumentStatus: Reference | null;
    qbooksVendor: Reference | null;
    qbooksCustomer: Reference | null;
    qbooksEmployee: Reference | null;
    qbooksProduct: Reference | null;
    qbooksPayeeVendor: Reference | null;
    qbooksPayeeCustomer: Reference | null;
    qbooksPayeeEmployee: Reference | null;
    qbooksPaymentAccount: Reference | null;
    qbooksClass: Reference | null;
    qbooksDepartment: Reference | null;
    qbooksAccount: Reference | null;
    qbooksCurrency: Reference | null;
}

export type NetSuiteSearchCustomField = Reference & {
    fieldId: string;
    valueRefId: string;
};

interface SearchParametersNetSuite {
    // NetSuite filters
    netSuiteTransactionNumber: string;
    netSuiteReference: string;
    netSuitePayee: Reference | null;
    netSuiteVendor: Reference | null;
    netSuiteDueDateGreaterEquals: string | null;
    netSuiteDueDateLessEquals: string | null;
    netSuiteDateGreaterEquals: string | null;
    netSuiteDateLessEquals: string | null;
    netSuiteCategory: Reference | null;
    netSuiteAccount: Reference | null;
    netSuiteItem: Reference | null;
    netSuiteDepartment: Reference | null;
    netSuiteClass: Reference | null;
    netSuiteLocation: Reference | null;
    netSuitePOReference: string;
    netSuitePOVendorReference: string;
    netSuiteEmployee: Reference | null;
    netSuiteCustomer: Reference | null;
    netSuiteTaxCode: Reference | null;
    netSuiteCustomFields: NetSuiteSearchCustomField[];
}

interface SearchParametersStandalone {
    // Standalone filters
    standaloneRequestName: string;
    standaloneRequestDescription: string;
}

interface SearchParametersDear {
    // Dear filters
    dearOrderNumber: string;
    dearLocation: Reference | null;
    dearSuplier: Reference | null;
    dearProduct: Reference | null;
    dearInventoryAccount: Reference | null;
    dearOrderDateGreaterEquals: string | null;
    dearOrderDateLessEquals: string | null;
    dearRequiredByGreaterEquals: string | null;
    dearRequiredByLessEquals: string | null;
}

export interface SearchParameters
    extends SearchParametersXero,
        SearchParametersQbooks,
        SearchParametersStandalone,
        SearchParametersNetSuite,
        SearchParametersDear {
    // Mandatory
    company: CompanySearchContext | null;
    template: TemplateReference | null;

    // General filters
    requestor: domain.User | null;
    requestStatusV2: domain.RequestStatusV2 | null;
    amountGreaterEquals: number | null;
    amountLessEquals: number | null;
    documentDateGreaterEquals: string | null;
    documentDateLessEquals: string | null;
}

export function getEmptyXeroParams(): SearchParametersXero {
    return {
        xeroDocumentNumber: '',
        xeroDocumentReference: '',
        xeroTrackingCategories: [],
        xeroAccount: null,
        xeroItem: null,
        xeroContact: null,
        airwallexScheduledPaymentDateGreaterEquals: null,
        airwallexScheduledPaymentDateLessEquals: null,
        xeroNarration: '',
    };
}

export function getEmptyQBooksParams(): SearchParametersQbooks {
    return {
        qbooksPoNumber: '',
        qbooksDocumentStatus: null,
        qbooksVendor: null,
        qbooksCustomer: null,
        qbooksEmployee: null,
        qbooksProduct: null,
        qbooksPayeeVendor: null,
        qbooksPayeeCustomer: null,
        qbooksPayeeEmployee: null,
        qbooksPaymentAccount: null,
        qbooksClass: null,
        qbooksDepartment: null,
        qbooksAccount: null,
        qbooksCurrency: null,
    };
}

export function getEmptyNetSuiteParams(): SearchParametersNetSuite {
    return {
        netSuiteTransactionNumber: '',
        netSuiteReference: '',
        netSuitePayee: null,
        netSuiteVendor: null,
        netSuiteDueDateGreaterEquals: null,
        netSuiteDueDateLessEquals: null,
        netSuiteDateGreaterEquals: null,
        netSuiteDateLessEquals: null,
        netSuiteCategory: null,
        netSuiteAccount: null,
        netSuiteItem: null,
        netSuiteDepartment: null,
        netSuiteClass: null,
        netSuiteLocation: null,
        netSuitePOReference: '',
        netSuitePOVendorReference: '',
        netSuiteEmployee: null,
        netSuiteCustomer: null,
        netSuiteTaxCode: null,
        netSuiteCustomFields: [],
    };
}

export function getEmptyDearParams(): SearchParametersDear {
    return {
        dearOrderNumber: '',
        dearLocation: null,
        dearSuplier: null,
        dearProduct: null,
        dearInventoryAccount: null,
        dearOrderDateGreaterEquals: null,
        dearOrderDateLessEquals: null,
        dearRequiredByGreaterEquals: null,
        dearRequiredByLessEquals: null,
    };
}

export function getEmptyStandaloneParams(): SearchParametersStandalone {
    return {
        standaloneRequestName: '',
        standaloneRequestDescription: '',
    };
}

export function createEmptySearchParams(): SearchParameters {
    return {
        company: null,
        template: null,

        requestor: null,
        requestStatusV2: null,

        amountGreaterEquals: null,
        amountLessEquals: null,
        documentDateGreaterEquals: null,
        documentDateLessEquals: null,

        ...getEmptyStandaloneParams(),
        ...getEmptyXeroParams(),
        ...getEmptyQBooksParams(),
        ...getEmptyNetSuiteParams(),
        ...getEmptyDearParams(),
    };
}
